import sanitizehtml from 'sanitize-html';
import { Role } from 'domains/models/event_type_user/role';
import { UserType } from 'domains/models/event_type_user/user_type';
import { LocationType } from 'domains/models/event_type/locationType';
import {
  getDateTime,
  formatToTime,
  DateTime,
  currentTimeZone,
  dateTimeToSQL,
  formatToISO,
} from 'infras/datetime';
import { CalendarEventListItem } from 'presentations/api/schema/calendar_event';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import {
  AdminEventUserListItem,
  EventUserListItem,
} from 'presentations/api/schema/event_user';
import { EventListItem, EventExportItem } from 'presentations/api/schema/event';
import { translate } from 'infras/i18n';
import { formatHostName, formatLocationNameByEvent } from './formatter';
import EventContact from 'domains/models/event_contact';
import {
  dashboardHostEventPath,
  dashboardHostIntegrationEventPath,
  eventPath,
  webPath,
} from 'presentations/routes/path';
import Event from 'domains/models/event';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import { SourceType } from 'domains/models/event_source/sourceType';
import Project from 'domains/models/project';
import { NextRouter } from 'next/router';
import { ViewerItem } from 'presentations/api/schema/user';
import { SearchEventListItem } from 'presentations/api/schema/search';
import Answer from 'domains/models/answer';
import { formatGuestAnswer } from 'presentations/api/schema/answer';
import { Status } from 'domains/models/event/status';
import { Transparency } from 'domains/models/event/transparency';
import { Priority } from 'domains/models/event/priority';
import { attention, gray02, primary } from '../styles/color';
import { Label } from 'domains/models/event/label';
import { VisibilityType } from 'domains/models/event/visibilityType';
import { eventDetailPath } from 'presentations/routes/path/event';
import EventTracking from 'domains/models/event_tracking';
import { IntegrationMergedEventListItem } from 'presentations/api/schema/calendar_event/integrationMerged';

export function splitHostAndUsersFromEvent(
  eventUsers: EventUserListItem[] | AdminEventUserListItem[],
) {
  if (!eventUsers) {
    return { host: null, users: [] };
  }
  let host = null;
  const users = [];
  for (const user of eventUsers) {
    if (user.userType === UserType.Host) {
      if (user.role === Role.Admin) host = user;
      else users.push(user);
    }
  }
  return { host, users };
}

export function isEventListItem(arg: any): arg is EventListItem {
  return arg.status !== undefined;
}
export function isCalendarEventListItem(
  arg: any,
): arg is CalendarEventListItem {
  return arg.calendarId !== undefined;
}

export function keyForMergedEventListItem(
  mergedEvent: IntegrationMergedEventListItem,
) {
  if (mergedEvent.calendarSourceType === CalendarSourceType.Integration) {
    const event = mergedEvent.event as CalendarEventListItem;
    return `${mergedEvent.calendarSourceType}-${event.calendarId}${event.uid}`;
  } else {
    const event = mergedEvent.event as EventListItem;
    return `${mergedEvent.calendarSourceType}-${event.uid}-${
      event.isHost ? 'host' : 'guest'
    }`;
  }
}

export function viewerOfEvent(
  event: EventExportItem | EventListItem | CalendarEventListItem,
  userId: string,
) {
  return isCalendarEventListItem(event)
    ? null
    : event.users?.find((u) => u.uid === userId);
}

export function isLocationRemote(
  event:
    | EventExportItem
    | EventListItem
    | CalendarEventListItem
    | SearchEventListItem,
) {
  return isCalendarEventListItem(event)
    ? null
    : event.locationId !== LocationType.InPerson &&
        event.locationId !== LocationType.Custom &&
        event.locationId !== LocationType.AskInvitee;
}

export function getEventTime(
  event: EventListItem | CalendarEventListItem,
  dateStart: DateTime,
  dateEnd: DateTime,
  timezone?: string,
) {
  const timeZone = timezone || currentTimeZone();
  const startedAt = getDateTime(event.startedAt, timeZone);
  const endedAt = getDateTime(event.endedAt, timeZone);

  let startedAtString = formatToTime(startedAt);
  const endedAtString = formatToTime(endedAt);
  // 終日のデータだけUTCとなるため、UTC換算で0時〜0時になっているかで判定
  const isAllDay = event.isAllDay;
  if (!isAllDay) {
    if (startedAt <= dateStart) {
      startedAtString = '';
    }
  }

  return {
    isAllDay,
    startedAt: isAllDay ? startedAt.toUTC().valueOf() : startedAt.valueOf(),
    startedAtString,
    endedAt: isAllDay ? endedAt.toUTC().valueOf() : endedAt.valueOf(),
    endedAtString,
  };
}

export function sortEventsByDate(
  a: { isAllDay: boolean; startedAt: number; endedAt: number },
  b: { isAllDay: boolean; startedAt: number; endedAt: number },
) {
  if (a.isAllDay && !b.isAllDay) {
    return -1;
  } else if (!a.isAllDay && b.isAllDay) {
    return 1;
  } else if (a.startedAt === b.startedAt) {
    return a.endedAt - b.endedAt;
  } else {
    return a.startedAt - b.startedAt;
  }
}

export function getEventTitle(event: EventListItem | EventExportItem) {
  if (event.sourceType === SourceType.EventType) {
    return getEventUserName(event);
  } else {
    return event.name;
  }
}

export function getEventUserName(
  event: EventListItem | EventExportItem,
  noCompany?: boolean,
) {
  if (event.isBuffer) {
    return event.name;
  }
  if (event.isHost) {
    if (noCompany) {
      return event.contact?.name || null;
    } else {
      const names = [event.contact?.name];
      if (event.contact?.company) names.unshift(event.contact?.company);
      return names.join(' | ');
    }
  } else {
    let host: EventTypeUserListItem | undefined;
    if (Array.isArray(event.users)) {
      for (const user of event.users) {
        if (user.role === Role.Admin) {
          host = user;
        }
      }
    }
    if (!host) return null;
    return formatHostName(
      event.hostOperationType,
      host?.name,
      event.team?.name || null,
    );
  }
}

interface FormatCalendarBodyProps {
  event: Event;
  eventContact: EventContact | null;
  customContent: string | null;
  locale: string;
  zoomMeetingId?: string | null;
  zoomMeetingPassword?: string | null;
  answers?: Answer[] | null;
  isShowPoweredBy?: boolean;
  nl?: string;
}

export function formatCalendarBody(props: FormatCalendarBodyProps) {
  const { event, eventContact, customContent, locale, answers } = props;

  const zoomMeetingId = props.zoomMeetingId || null;
  const zoomMeetingPassword = props.zoomMeetingPassword || null;
  const nl = props.nl || '<br />';

  const locationName = formatLocationNameByEvent(event);
  const location = translate(
    'mailer.CalendarInvite.location',
    {
      location: locationName,
    },
    true,
  );
  const access = translate('mailer.CalendarInvite.access', {
    access: event.access,
  });
  const phone = translate('mailer.CalendarInvite.phone', {
    phoneNumber: event.phoneNumber,
  });
  const videoUrl = translate('mailer.CalendarInvite.videoUrl', {
    url: event.url,
  });
  const url = translate('mailer.CalendarInvite.url', {
    url: webPath(
      event.isSeminar
        ? dashboardHostEventPath(event.uid)
        : eventPath(event.uid),
      locale,
    ),
  });
  const meetingId = translate('mailer.CalendarInvite.zoomMeetingId', {
    id: zoomMeetingId,
  });
  const passCode = translate('mailer.CalendarInvite.zoomMeetingPassword', {
    passcode: zoomMeetingPassword,
  });
  const title =
    event.sourceType === SourceType.EventType && !event.isSeminar
      ? translate('mailer.CalendarInvite.title', {
          eventName: event.name,
          contactName: eventContact ? eventContact.name : '',
        })
      : translate('mailer.CalendarInvite.eventTitle', {
          eventName: event.name,
        });
  const message = translate(
    'mailer.CalendarInvite.message',
    {
      message: eventContact ? eventContact.message : '',
    },
    true,
  );
  let body = '';
  if (customContent) body += `${customContent}${nl}${nl}`;
  if (url) body += `${url}${nl}`;
  if (title) body += `${title}${nl}`;
  if (locationName) body += `${location}${nl}`;
  if (event.url) body += `${videoUrl}${nl}`;
  if (zoomMeetingId) body += `${meetingId}${nl}`;
  if (zoomMeetingPassword) body += `${passCode}${nl}`;
  if (event.access) body += `${access}${nl}`;
  if (event.phoneNumber) body += `${phone}${nl}`;
  if (eventContact && eventContact.message) body += `${message}${nl}`;
  if (answers && answers.length > 0) {
    for (const answer of answers) {
      const a =
        formatGuestAnswer(answer) || translate('mailer.EventDetail.noAnswer');
      body += `${answer.question}: ${a}${nl}`;
    }
  }
  if (props.isShowPoweredBy) {
    body += `${nl}${translate('mailer.CalendarInvite.poweredBy.0')}${nl}`;
    body += `${translate('mailer.CalendarInvite.poweredBy.1')}${nl}`;
  }
  return body;
}

export function formatExportHeader() {
  const headers = [
    translate('eventExport.eventUid'),
    translate('eventExport.eventName'),
    translate('eventExport.hostName'),
    translate('eventExport.projectUid'),
    translate('eventExport.projectName'),
    translate('eventExport.guestID'),
    translate('eventExport.guestName'),
    translate('eventExport.guestEmail'),
    translate('eventExport.guestTimeZone'),
    translate('eventExport.startedAt'),
    translate('eventExport.endedAt'),
    translate('eventExport.location'),
    translate('eventExport.createdAt'),
    translate('eventExport.canceledAt'),
    translate('eventExport.canceledBy'),
    translate('eventExport.cancelReason'),
    translate('eventExport.totalAmount'),
    translate('eventExport.currency'),
    translate('eventExport.meetingNote'),
    translate('eventExport.utmCampaign'),
    translate('eventExport.utmSource'),
    translate('eventExport.utmMedium'),
    translate('eventExport.utmContent'),
    translate('eventExport.utmTerm'),
  ];
  return headers;
}

export function formatExportBody(
  eventItem: EventExportItem,
  eventContact: EventContact,
  project: Project | null,
  pageContent: string | null,
  eventTracking: EventTracking | null,
) {
  let location = null;
  switch (eventItem.locationId) {
    case LocationType.InPerson:
      location = eventItem.address;
      break;
    case LocationType.Phone:
      location = eventItem.phoneNumber;
      break;
    case LocationType.Custom:
      location = eventItem.customLocation;
      break;
    case LocationType.GoogleMeet:
    case LocationType.Zoom:
    case LocationType.Teams:
      const eventMeeting = eventItem.meetings.find((m) => m.isPrimary);
      location = eventMeeting
        ? translate(`locationType_${eventItem.locationId}`) +
          ': ' +
          eventMeeting.url
        : eventItem.url;
      break;
    default:
      location = '';
      break;
  }

  const meetingNote = pageContent
    ? sanitizehtml(pageContent, {
        allowedTags: [],
        allowedAttributes: {},
        allowedSchemes: [],
        allowedSchemesByTag: {},
        allowedSchemesAppliedToAttributes: [],
      })
    : null;

  let hostName;
  if (Array.isArray(eventItem.users)) {
    for (const user of eventItem.users) {
      if (user.role === Role.Admin) {
        hostName = user.name;
        break;
      }
    }
  }

  return {
    eventUid: eventItem.uid,
    eventName: eventItem.name,
    hostName,
    projectUid: project?.uid || null,
    projectName: project?.name || null,
    guestID: eventContact.userId,
    guestName: eventContact.name,
    guestEmail: eventContact.email,
    guestTimeZone: eventItem.timeZone,
    startedAt: dateTimeToSQL(
      getDateTime(eventItem.startedAt, eventItem.defaultTimeZone!),
    ),
    endedAt: dateTimeToSQL(
      getDateTime(eventItem.endedAt, eventItem.defaultTimeZone!),
    ),
    location,
    createdAt: dateTimeToSQL(
      getDateTime(eventItem.createdAt, eventItem.defaultTimeZone!),
    ),
    canceledAt: eventItem.cancelledAt
      ? dateTimeToSQL(
          getDateTime(eventItem.cancelledAt, eventItem.defaultTimeZone!),
        )
      : null,
    canceledBy: eventItem.cancelledBy
      ? translate(`UserType.${eventItem.cancelledBy}`)
      : null,
    cancelReason: eventItem.cancelReason,
    totalAmount: eventItem.payment?.totalAmount || null,
    currency: eventItem.payment?.currency || null,
    meetingNote,
    utmCampaign: eventTracking?.campaign ?? null,
    utmSource: eventTracking?.source ?? null,
    utmMedium: eventTracking?.medium ?? null,
    utmContent: eventTracking?.content ?? null,
    utmTerm: eventTracking?.term ?? null,
  };
}

export const openMergedEvent = async (
  mergedEvent: IntegrationMergedEventListItem,
  userId: string,
  router: NextRouter,
) => {
  switch (mergedEvent?.calendarSourceType) {
    case CalendarSourceType.Jicoo: {
      const event = mergedEvent.event as EventListItem;
      const url = eventDetailPath(event, router.asPath);
      router.push(url);
      break;
    }
    case CalendarSourceType.Integration: {
      router.push(getIntegrationEventUrl(mergedEvent, userId, router.asPath));
      break;
    }
    default:
      break;
  }
};

export const getIntegrationEventUrl = (
  mergedEvent: IntegrationMergedEventListItem,
  userId: string,
  asPath: string,
) => {
  const calendarEvent = mergedEvent.event as CalendarEventListItem;
  return dashboardHostIntegrationEventPath(
    {
      calendarId: String(calendarEvent.calendarId),
      uid: String(calendarEvent.uid),
      userId: String(userId),
      startedAt: formatToISO(calendarEvent.startedAt),
      endedAt: formatToISO(calendarEvent.endedAt),
    },
    asPath,
  );
};

export const filterAndConvertByDate = (
  date: DateTime,
  timeZone: string,
  calendarOffset: number,
  events: IntegrationMergedEventListItem[],
  filterTransparent?: boolean,
) => {
  const result = events
    .filter((mergedEvent) => {
      if (mergedEvent.calendarSourceType !== CalendarSourceType.Jicoo) {
        return true;
      }

      const event = mergedEvent.event as EventListItem;
      // Event.statusでフィルタ
      if (event.status === Status.Reserved) {
        return false;
      } else if (event.status === Status.Open) {
        // 公開中の予定は表示
        return true;
      } else {
        return false;
      }
    })
    .map((mergedEvent) => {
      let eventStartedAt = getDateTime(
        mergedEvent.event.startedAt,
        timeZone,
      ).plus({
        minutes: calendarOffset,
      });
      let eventEndedAt = getDateTime(mergedEvent.event.endedAt, timeZone).plus({
        minutes: calendarOffset,
      });
      if (mergedEvent.event.isAllDay) {
        // 外部の終日予定がUTCで来るようなので調整する
        const timeOffset = new Date().getTimezoneOffset() / 60;
        eventStartedAt = eventStartedAt
          .minus({
            hour: -timeOffset,
          })
          .startOf('day');
        eventEndedAt = eventEndedAt
          .minus({
            hour: -timeOffset,
          })
          .startOf('day');
      }
      return {
        mergedEvent: mergedEvent,
        startedAt: eventStartedAt,
        endedAt: eventEndedAt,
      };
    })
    .filter((eventViewModel) => {
      // 指定日の予定のみにフィルタ
      const diffStartedAt = date.diff(eventViewModel.startedAt, 'day').days;
      const diffEndedAt = date.diff(eventViewModel.endedAt, 'day').days;
      return eventViewModel.mergedEvent.event.isAllDay
        ? diffStartedAt >= 0 && diffEndedAt < 0
        : (diffStartedAt <= 0 && diffStartedAt > -1) ||
            (diffStartedAt > 0 && diffEndedAt <= 0);
    });

  return filterTransparent
    ? result.filter(
        (event) =>
          event.mergedEvent.event.transparency !== Transparency.Transparent,
      )
    : result;
};

export const getPriorityOptions = (t: Function) => {
  return [
    {
      icon: 'flag-triangle',
      iconColor: attention,
      text: t(`priority_${Priority.High}`),
      value: Priority.High,
    },
    {
      icon: 'flag-triangle',
      iconColor: primary,
      text: t(`priority_${Priority.Middle}`),
      value: Priority.Middle,
    },
    {
      icon: 'flag-triangle',
      iconColor: gray02,
      text: t(`priority_${Priority.Low}`),
      value: Priority.Low,
    },
    {
      text: t(`priority_none`),
      value: null,
    },
  ];
};

export const getTagOptions = (t: Function) => {
  return [
    {
      text: t(`tag_${Label.TeamMeeting}`),
      value: Label.TeamMeeting,
    },
    {
      text: t(`tag_${Label.SoloWork}`),
      value: Label.SoloWork,
    },
    {
      text: t('tag_none'),
      value: null,
    },
  ];
};

export const canShowEventDetail = (
  visibilityType: VisibilityType | null | undefined,
  users: { uid: string }[],
  viewer: ViewerItem,
) => {
  return (
    visibilityType === VisibilityType.Default ||
    users.some((eventUser) => eventUser.uid === viewer.uid)
  );
};
